import { useLingui } from '@lingui/react/macro';
import { type FC, useState } from 'react';
import { Link } from '@/base/router';
import { useCoin } from '@/shared/api';
import { Banner } from '@/shared/Banner';
import { Button } from '@/shared/Button';
import { Digits } from '@/shared/Digits';
import { Input } from '@/shared/Input';
import { PriceInput } from '@/shared/PriceInput';
import { Widget } from '@/shared/Widget';
import { CoinSelect } from '../CoinSelect';
import { useTimeMachine } from '../useTimeMachine';

type CoinTimeMachineProps = {
  slug?: string;
};

export const CoinTimeMachine: FC<CoinTimeMachineProps> = ({ slug }) => {
  const { data: coin } = useCoin(slug?.toUpperCase());
  const [selectedCoin, setSelectedCoin] = useState<string>(
    coin?.slug ?? slug ?? '',
  );

  const { t } = useLingui();
  const { data: coinData, isLoading: isLoadingCoin } = useCoin(selectedCoin);

  const [amount, setAmount] = useState<string>('');
  const [days, setDays] = useState<string>('');

  const amountNum = parseFloat(amount) || 0;
  const daysNum = parseInt(days, 10) || 0;
  const currentPrice = Number(coinData?.price_usdt ?? 0);

  const {
    price: priceDaysAgo,
    isLoading,
    error,
  } = useTimeMachine({
    coinId: coinData?.coingecko_name || coinData?.name.toLowerCase(),
    days: daysNum > 0 ? daysNum : undefined,
  });

  const hasHistoricalPrice = typeof priceDaysAgo === 'number';
  const historicalPrice = hasHistoricalPrice ? priceDaysAgo : undefined;
  let balanceEstimation: number | undefined;
  if (
    typeof historicalPrice === 'number' &&
    historicalPrice !== 0 &&
    !Number.isNaN(amountNum)
  ) {
    const pastPrice = historicalPrice as number;
    balanceEstimation = (amountNum / pastPrice) * currentPrice;
  }
  const profitPercent =
    typeof historicalPrice === 'number' && historicalPrice !== 0
      ? ((currentPrice - historicalPrice) / historicalPrice) * 100
      : undefined;
  const priceDaysAgoDisplay = historicalPrice ?? currentPrice;

  return (
    <Widget className="space-y-6">
      <h2 className="text-lg font-medium text-content-primary mb-1">{t`Time Machine`}</h2>
      <p className="text-sm text-content-secondary">
        {t`See how much you would have earned if you had invested in this coin days ago.`}
      </p>

      <div className="flex flex-col desktop:flex-row gap-widget">
        <div className="flex-1 space-y-4">
          <div>
            <CoinSelect
              value={selectedCoin}
              onChange={(newCoin) => {
                if (newCoin) setSelectedCoin(newCoin);
              }}
              placeholder={t`Select Coin`}
              className="w-full"
              required
              filteredKeys={['IQD', 'USDT']}
            />
          </div>

          <div>
            <PriceInput
              size={12}
              value={amount}
              onChange={(newValue) => setAmount(newValue ?? '')}
              placeholder={t`Amount (USDT)`}
              className="w-full"
              precision={2}
            />
          </div>

          <div>
            <Input
              size={12}
              value={days}
              onChange={(newValue) => setDays(newValue ?? '')}
              type="number"
              placeholder={t`Day`}
              className="w-full"
              autoComplete="off"
              pattern="[0-9]*"
            />
          </div>
        </div>

        <div className="flex-1 border border-border-disabled rounded-xl p-4 space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-content-primary">
              {t`Balance Estimation`}
            </span>
            <div className="flex items-center gap-3">
              {typeof profitPercent === 'number' && (
                <div className="flex items-center gap-1">
                  <span
                    className={`text-xs font-medium ${profitPercent >= 0 ? 'text-content-positive' : 'text-content-negative'}`}
                    dir="ltr"
                  >
                    {profitPercent >= 0 ? '+' : ''}
                    {profitPercent.toFixed(2)}%
                  </span>
                </div>
              )}
              <Digits
                value={balanceEstimation?.toFixed(2)}
                coin="USDT"
                className="text-sm text-content-secondary"
              />
            </div>
          </div>

          <div className="h-px bg-border-disabled" />

          <div className="flex justify-between items-center">
            <span className="text-sm text-content-primary">
              {t`Current Price`}
            </span>
            <Digits
              value={currentPrice}
              coin="USDT"
              className="text-sm text-content-secondary"
            />
          </div>

          <div className="flex justify-between items-center">
            <span className="text-sm text-content-primary">
              {daysNum > 0
                ? t`Price ${daysNum} Days Ago`
                : t`Price --- Days Ago`}
            </span>
            <Digits
              value={priceDaysAgoDisplay?.toFixed(2)}
              coin="USDT"
              className="text-sm text-content-secondary"
            />
          </div>
          {(isLoading || isLoadingCoin) && (
            <p className="text-xs text-content-tertiary">
              {t`Loading historical price...`}
            </p>
          )}
        </div>
      </div>

      <div className="flex flex-col desktop:flex-row gap-4 desktop:justify-between">
        <div>
          {error && (
            <Banner variant="negative" size={5} className="w-full">
              {error}
            </Banner>
          )}
        </div>
        <Link to={`/trade/instant/?coin=${selectedCoin}`} className="contents">
          <Button size={12} variant="primary" className="desktop:w-auto w-full">
            {t`Buy ${coinData?.name ?? selectedCoin}`}
          </Button>
        </Link>
      </div>
    </Widget>
  );
};
